import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { SlidersHorizontal, TrendingUp, TrendingDown, RotateCcw, Loader2 } from "lucide-react";

export default function Simulator({ propId, date, lang = "it", baselinePrice }) {
  const L = (it, en) => (lang === "it" ? it : en);
  const [open, setOpen] = useState(false);
  const [comps, setComps] = useState([]);
  const [st, setSt] = useState({ price_override: "", rooms_delta: 0, occupancy_override: "", demand_delta: 0, add_event: false, remove_competitor_id: "" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open || !propId) return;
    api.get(`/properties/${propId}/competitor-reliability?lang=${lang}`)
      .then((r) => setComps(r.data?.competitors || [])).catch(() => setComps([]));
  }, [open, propId]);

  const run = () => {
    if (!propId || !date) return;
    setLoading(true);
    const body = { date, lang,
      price_override: st.price_override ? Number(st.price_override) : null,
      rooms_delta: Number(st.rooms_delta) || 0,
      occupancy_override: st.occupancy_override === "" ? null : Number(st.occupancy_override),
      demand_delta: Number(st.demand_delta) || 0,
      add_event: st.add_event, remove_competitor_id: st.remove_competitor_id || null };
    api.post(`/properties/${propId}/simulate`, body)
      .then((r) => setRes(r.data)).catch(() => setRes(null)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(run, 450);
    return () => clearTimeout(timer.current);
  }, [open, st, date]);

  const reset = () => setSt({ price_override: "", rooms_delta: 0, occupancy_override: "", demand_delta: 0, add_event: false, remove_competitor_id: "" });
  const upd = (k, v) => setSt((s) => ({ ...s, [k]: v }));

  const sim = res?.simulated;
  const delta = sim?.delta_vs_baseline;

  return (
    <div className="mt-6 pt-4 border-t border-border" data-testid="simulator">
      <button onClick={() => setOpen((o) => !o)} data-testid="sim-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
        <SlidersHorizontal className="w-4 h-4" /> {L("Simulatore what-if", "What-if simulator")} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="mt-3 grid md:grid-cols-2 gap-5">
          {/* controls */}
          <div className="space-y-3 text-sm">
            <Field label={L("Prezzo impostato (€)", "Set price (€)")}>
              <input type="number" data-testid="sim-price" value={st.price_override} onChange={(e) => upd("price_override", e.target.value)}
                placeholder={baselinePrice ? `${baselinePrice}` : "—"} className="w-28 px-2 py-1 rounded border border-border bg-background" />
            </Field>
            <Field label={L("Camere (apri/chiudi)", "Rooms (open/close)")}>
              <Stepper value={st.rooms_delta} onChange={(v) => upd("rooms_delta", v)} testid="sim-rooms" />
            </Field>
            <Field label={L(`Occupazione: ${st.occupancy_override === "" ? "auto" : st.occupancy_override + "%"}`, `Occupancy: ${st.occupancy_override === "" ? "auto" : st.occupancy_override + "%"}`)}>
              <input type="range" min="0" max="100" step="5" data-testid="sim-occ" value={st.occupancy_override === "" ? 50 : st.occupancy_override}
                onChange={(e) => upd("occupancy_override", Number(e.target.value))} className="w-40" />
            </Field>
            <Field label={L(`Domanda: ${st.demand_delta > 0 ? "+" : ""}${st.demand_delta}`, `Demand: ${st.demand_delta > 0 ? "+" : ""}${st.demand_delta}`)}>
              <input type="range" min="-30" max="30" step="5" data-testid="sim-demand" value={st.demand_delta}
                onChange={(e) => upd("demand_delta", Number(e.target.value))} className="w-40" />
            </Field>
            <Field label={L("Aggiungi evento", "Add event")}>
              <input type="checkbox" data-testid="sim-event" checked={st.add_event} onChange={(e) => upd("add_event", e.target.checked)} className="w-4 h-4" />
            </Field>
            <Field label={L("Rimuovi competitor", "Remove competitor")}>
              <select data-testid="sim-remove-comp" value={st.remove_competitor_id} onChange={(e) => upd("remove_competitor_id", e.target.value)}
                className="max-w-[200px] px-2 py-1 rounded border border-border bg-background text-xs">
                <option value="">{L("— nessuno —", "— none —")}</option>
                {comps.map((c) => <option key={c.competitor_id} value={c.competitor_id}>{c.name}</option>)}
              </select>
            </Field>
            <button onClick={reset} data-testid="sim-reset" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <RotateCcw className="w-3.5 h-3.5" /> {L("Ripristina", "Reset")}
            </button>
          </div>

          {/* result */}
          <div className="bg-secondary/40 rounded-lg p-4" data-testid="sim-result">
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {L("Calcolo…", "Computing…")}</div>
            ) : !sim ? (
              <div className="text-sm text-muted-foreground">{L("Modifica i parametri per vedere il nuovo prezzo consigliato.", "Change parameters to see the new recommended price.")}</div>
            ) : (
              <>
                <div className="flex items-end gap-3">
                  <div>
                    <div className="text-[11px] uppercase text-muted-foreground">{L("Consigliato ora", "Recommended now")}</div>
                    <div className="text-3xl font-bold">€{sim.recommended_price}</div>
                  </div>
                  {delta != null && delta !== 0 && (
                    <div className={`flex items-center gap-1 font-bold mb-1 ${delta > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      {delta > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {delta > 0 ? "+" : ""}€{delta}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mb-1">{L("vs base", "vs base")} €{res.baseline.recommended_price}</div>
                </div>
                {sim.headline && <div className="text-sm mt-2">{sim.headline}</div>}
                {res.revenue_projection && (
                  <div className="mt-3 pt-3 border-t border-border text-sm" data-testid="sim-revenue">
                    <div className="text-[11px] uppercase text-muted-foreground mb-1">{L("Proiezione ricavi (stima)", "Revenue projection (estimate)")}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>{L("Al consigliato", "At recommended")} €{res.revenue_projection.at_recommended.price}: <b>€{res.revenue_projection.at_recommended.expected_revenue}</b> <span className="text-muted-foreground">({res.revenue_projection.at_recommended.expected_occupancy_pct}%)</span></div>
                      <div>{L("Al tuo prezzo", "At your price")} €{res.revenue_projection.at_your_price.price}: <b>€{res.revenue_projection.at_your_price.expected_revenue}</b> <span className="text-muted-foreground">({res.revenue_projection.at_your_price.expected_occupancy_pct}%)</span></div>
                    </div>
                    <div className={`mt-1 font-semibold ${res.revenue_projection.difference_eur >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      {L("Differenza", "Difference")}: {res.revenue_projection.difference_eur >= 0 ? "+" : ""}€{res.revenue_projection.difference_eur}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{res.revenue_projection.note}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>{children}
  </div>
);

const Stepper = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <button data-testid={`${testid}-minus`} onClick={() => onChange(value - 1)} className="w-7 h-7 rounded border border-border hover:bg-accent">−</button>
    <span className="w-8 text-center font-bold">{value > 0 ? "+" : ""}{value}</span>
    <button data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)} className="w-7 h-7 rounded border border-border hover:bg-accent">+</button>
  </div>
);
